import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { debug } from "../../../utils/debugLogger";
import { validateOAuthState } from "../../../utils/oauthState";
import { readPersistedWorkspace } from "../../../utils/workspacePersistence";
import {
  clearOAuthCustomerId,
  readOAuthCustomerId,
} from "../../site-connections/siteConnectionSession";
import {
  TIKTOK_OAUTH_STATE_PROVIDER,
  exchangeTikTokCode,
} from "../services/tiktokAuthentication";
import { getTikTokRedirectUri } from "../config/tiktokEnv";

export const TIKTOK_PENDING_TOKEN_KEY = "tiktok_pending_token";
const CONNECT_TAB = "/social-posts?tab=connect&section=social";

/**
 * Recover the Cognito token after TikTok's redirect wipes React context.
 * Priority: live AuthContext, tiktok_pending_token, generic idToken. Same helper
 * as LinkedInCallback/GHLCallback: this route is public because AuthContext is
 * not reliably rehydrated on return, and a guard would bounce to /login and
 * discard the single-use authorization code.
 */
const resolveToken = (authIdToken: string): string => {
  if (authIdToken) return authIdToken;
  const pending = localStorage.getItem(TIKTOK_PENDING_TOKEN_KEY);
  if (pending) return pending;
  return localStorage.getItem("idToken") ?? "";
};

const TikTokCallback: React.FC = () => {
  const navigate = useNavigate();
  const { idToken: authIdToken, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // The code is single-use and the state nonce is consumed on validation, so
  // the exchange must run exactly once (also under StrictMode's double effects).
  const started = useRef(false);

  useEffect(() => {
    if (isLoading || started.current) return;
    started.current = true;

    const finishWithError = (message: string, cause?: unknown) => {
      if (cause) debug.error("TikTok callback failed:", cause);
      clearOAuthCustomerId(TIKTOK_OAUTH_STATE_PROVIDER);
      localStorage.removeItem(TIKTOK_PENDING_TOKEN_KEY);
      setError(message);
    };

    const params = new URLSearchParams(window.location.search);
    const stateOk = validateOAuthState(TIKTOK_OAUTH_STATE_PROVIDER, params.get("state"));

    // TikTok reports a declined consent screen as error=access_denied (+ description).
    const oauthError = params.get("error");
    if (oauthError) {
      finishWithError(
        oauthError === "access_denied"
          ? "TikTok access was not granted. You can try connecting again."
          : "TikTok couldn't complete the connection. Please try connecting again."
      );
      return;
    }
    // CSRF: the state returned must match the one stored before redirecting.
    if (!stateOk) {
      finishWithError("Security check failed (invalid OAuth state). Please try connecting again.");
      return;
    }
    const code = params.get("code");
    if (!code) {
      finishWithError("No authorization code received. Please try connecting again.");
      return;
    }
    const customerId = readOAuthCustomerId(TIKTOK_OAUTH_STATE_PROVIDER) || readPersistedWorkspace().id;
    if (!customerId) {
      finishWithError("No business profile selected. Please choose one and connect again.");
      return;
    }
    const token = resolveToken(authIdToken);
    if (!token) {
      finishWithError("Please log in first, then connect TikTok.");
      return;
    }

    exchangeTikTokCode(customerId, code, getTikTokRedirectUri(), token)
      .then((res) => {
        clearOAuthCustomerId(TIKTOK_OAUTH_STATE_PROVIDER);
        localStorage.removeItem(TIKTOK_PENDING_TOKEN_KEY);
        navigate(CONNECT_TAB, {
          replace: true,
          state: {
            success: true,
            message: res.display_name
              ? `TikTok connected as ${res.display_name}.`
              : "TikTok connected.",
          },
        });
      })
      .catch((err: unknown) =>
        finishWithError(
          err instanceof Error ? err.message : "Failed to connect TikTok. Please try again.",
          err
        )
      );
  }, [isLoading, authIdToken, navigate]);

  const goToConnect = () => navigate(CONNECT_TAB);

  return (
    <div className="flex flex-col min-h-screen p-4">
      <div className="flex flex-col items-center justify-center flex-1">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8 text-center">
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Connect TikTok</h1>
          {error ? (
            <>
              <p role="alert" className="text-red-600 mb-4">
                {error}
              </p>
              <button
                type="button"
                onClick={goToConnect}
                className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover"
              >
                Return to Connect Page
              </button>
            </>
          ) : (
            <p className="text-gray-600">Connecting your TikTok account...</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TikTokCallback;
